import React, { useState, Fragment, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import BlazeButton from '@blaze-react/button';
import { handleLogout } from '@blaze-cms/core-auth-ui';
import { useApolloClient } from '@apollo/client';
import PropTypes, { bool, string } from 'prop-types';
import { useStringTemplate } from '@blaze-cms/utils-handlebars';
import ClickWrapper from './ClickWrapper';
import BlazeLink from './BlazeLink';
import { withTitle } from '../HOC';
import { getClassModifiers } from '../utils';
import { LOGOUT } from '../constants';
import {
  renderChildren,
  updateChildrensParent,
  hasChildren,
  getClickWrapperOptions
} from '../helpers';

const Button = props => {
  const {
    type,
    text,
    url,
    icon,
    name,
    style,
    children,
    clickAction,
    childSeoVisibility,
    afterClickText,
    parent,
    ...otherProps
  } = props;
  const client = useApolloClient();
  const router = useRouter();
  const isLogoutButton = clickAction === LOGOUT;
  const [cookies] = useCookies();
  const { blaze_auth: blazeCookie } = cookies;
  const [showChildren, setShowChildren] = useState(false);
  const {
    data: [textToUse, afterClickTextToUse, urlToUse]
  } = useStringTemplate(parent, [text, afterClickText, url]);

  useEffect(
    () => {
      setShowChildren(false);
    },
    [router.asPath]
  );

  const checkIfButtonShouldRender = () => {
    if (isLogoutButton) return !!blazeCookie;
    if (!text && !icon) return false;
    return !!(url || hasChildren(children));
  };

  const logout = () => {
    handleLogout(client);
    if (hasChildren(children)) return setShowChildren(true);

    if (!urlToUse) return router.reload();

    return urlToUse.startsWith('/')
      ? router.push('/Resolver', urlToUse)
      : (window.location.href = urlToUse);
  };

  const handleClick = () => {
    if (isLogoutButton) logout();
    if (!url && !isLogoutButton) setShowChildren(!showChildren);
  };

  if (!checkIfButtonShouldRender()) return null;

  const buttonType = !text && icon ? 'icon' : type;
  const modifiers = getClassModifiers(buttonType, { icon, style, ...otherProps });
  const currentProps = url ? { href: urlToUse, title: name } : { name };
  const DynamicButton = url && !isLogoutButton ? BlazeLink : BlazeButton;
  updateChildrensParent(children, parent.itemId, parent.itemEntity);

  const renderedChildren = renderChildren(children, { onClose: handleClick });
  const { shouldUseClickWrapper, wrapperProps } = getClickWrapperOptions(children, props);
  const Wrapper = shouldUseClickWrapper ? ClickWrapper : Fragment;

  const contentClassName = classnames('button__content', {
    'button__content--show': showChildren
  });

  return (
    <Wrapper {...wrapperProps}>
      {childSeoVisibility && <div className={contentClassName}>{renderedChildren}</div>}
      {!childSeoVisibility && showChildren && renderedChildren}
      <DynamicButton className={modifiers} onClick={handleClick} {...currentProps}>
        {icon && <i className={icon} />}
        {showChildren ? afterClickTextToUse : textToUse}
      </DynamicButton>
    </Wrapper>
  );
};

Button.propTypes = {
  parent: PropTypes.object,
  clickAction: PropTypes.string,
  text: PropTypes.string,
  afterClickText: string,
  name: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  size: PropTypes.string,
  icon: PropTypes.string,
  style: PropTypes.string,
  childSeoVisibility: bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

Button.defaultProps = {
  parent: {},
  clickAction: '',
  text: '',
  afterClickText: '',
  name: '',
  type: '',
  url: null,
  size: '',
  icon: '',
  style: '',
  children: [],
  childSeoVisibility: false
};

export default withTitle(Button);
